import { useEffect, useState } from 'react';
import type { ApiClient, AussenBerichtsstand } from '../../api';
import { zeitpunkt } from '../sanitaer/kopf';

/**
 * Der PDF-Knopf EINES Außenbereichs — mit Dokumentnummer und Rechenstand.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM ER IN EINER EIGENEN DATEI STEHT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Weil er an fünf Stellen gebraucht wird und nicht an einer. Der Fachplaner
 * hat am 23.09.2026 gemeldet: „zudem fehlen je bereich in aussenanlagen die
 * pdf-exporte." Den Endpunkt gibt es für alle sieben Bereiche — erreichbar
 * war er über genau einen Knopf auf der Berechnungsseite, und der galt dem
 * Bereich, der dort gerade offen war.
 *
 * Jetzt steht er dort, wo der Bereich ist: an der Leitungsliste
 * (Freispiegel), an den Leerrohren, an den Hydranten (Löschwasser), an der
 * Versickerung — und in der Berichtsliste mit allen sieben.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE NUMMER GEHÖRT AN DEN KNOPF
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bis zum 23.09.2026 hieß er „als PDF". Wer ihn zweimal drückte, bekam zwei
 * Dokumente mit zwei Nummern und wusste nicht, ob sich etwas geändert hatte.
 * Die Dokumentnummer des letzten Berichts steht jetzt am Knopf, der
 * Rechenstand im Tooltip — und wenn seither neu gerechnet wurde, sagt der
 * Knopf das, statt eine überholte Nummer anzubieten.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ER HOLT SEINEN STAND SELBST, WENN IHN NIEMAND MITGIBT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Auf der Berechnungsseite liegt die Liste aller sieben Stände ohnehin vor;
 * dort wird sie als `stand` durchgereicht. Auf den vier Bestandsseiten liegt
 * sie nicht vor, und ein Knopf, der dafür eine eigene Ladeverwaltung in
 * jeder Seite verlangt, wird an drei von vier Stellen nicht eingebaut.
 *
 * Fehlt der Stand, ist das KEIN Fehler: Der Knopf heißt dann „als PDF" und
 * tut dasselbe. Was fehlt, ist die Nummer — nicht die Möglichkeit.
 */
export function AussenBerichtKnopf({
  api, projektId, bereich, stand, klein, selbstLaden,
}: {
  api: ApiClient;
  projektId: string;
  bereich: string;
  /** Der Stand, wenn der Aufrufer ihn schon hat. */
  stand?: AussenBerichtsstand | null;
  klein?: boolean;
  /** Den Stand selbst holen, wenn der Aufrufer keinen mitgibt. */
  selbstLaden?: boolean;
}) {
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [eigener, setEigener] = useState<AussenBerichtsstand | null>(null);

  useEffect(() => {
    if (!selbstLaden || stand) return;

    api.aussenBerichte(projektId)
      .then((x) => setEigener(
        x.bereiche.find((b) => b.bereich === bereich) ?? null))
      // Ohne Stand bleibt der Knopf ein Knopf. Eine Fehlermeldung an dieser
      // Stelle beträfe eine Nummer und nicht den Bericht.
      .catch(() => setEigener(null));
  }, [api, projektId, bereich, selbstLaden, stand]);

  const s = stand ?? eigener;

  const hole = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.aussenBerichtPdf(projektId, bereich);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  // Ohne Lauf gibt es nichts zu drucken. Der Knopf verschwindet nicht — er
  // sagt, was fehlt.
  if (s && !s.hatLauf) {
    return (
      <span className="schwach" style={{ fontSize: 11 }}>
        {s.titel}: noch nicht gerechnet
      </span>
    );
  }

  const beschriftung = laeuft
    ? 'erzeugt …'
    : s?.dokumentnummer
      ? (s.aktuell
        ? `PDF · ${s.dokumentnummer}`
        : `PDF neu · zuletzt ${s.dokumentnummer}`)
      : 'als PDF';

  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center',
                   flexWrap: 'wrap' }}>
      <button
        type="button"
        className={klein ? 'm-knopf m-knopf-still' : 'm-knopf'}
        disabled={laeuft}
        onClick={hole}
        title={s?.gerechnetAm
          ? `Rechenstand ${zeitpunkt(s.gerechnetAm)}`
            + (s.norm ? ` · ${s.norm}` : '')
          : undefined}
      >
        {beschriftung}
      </button>

      {s?.dokumentnummer && !s.aktuell && (
        <span className="schwach" style={{ fontSize: 11 }}>
          seit dem letzten Bericht neu gerechnet
        </span>
      )}

      {fehler && (
        <span className="hinweis fehler" style={{ fontSize: 11 }}>{fehler}</span>
      )}
    </span>
  );
}
